import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbDateStruct, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import { Tree } from '@s8l/client-tree-lib';
import { NestedPathElement } from 'src/app/components/nested-path-selector/nested-path-selector.component';
import { Achievement, AchievementDisplay, AchievementStat, CmsServerService, Instant } from 'src/app/services/jsonrpc/cms-server.service';
import { CmsService } from 'src/app/services/cms.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import { environment } from 'src/environments/environment';

interface StatOption {
  name: string;
  path?: boolean;
  stat: AchievementStat;
}

@Component({
  selector: 'app-achievement-edit',
  templateUrl: './achievement-edit.component.html',
  styleUrls: ['./achievement-edit.component.scss']
})
export class AchievementEditComponent implements OnInit {
  public readonly statOptions: StatOption[] = [
    {
      name: 'Total Online Time (sec)',
      stat: { event: 'time', selector: '', min_value: 0, max_value: 1000000000 },
      path: false
    },
    {
      name: 'Total Module Time (sec)',
      stat: { event: 'page-time', selector: '', min_value: 0, max_value: 1000000000 },
      path: true
    },
    {
      name: 'Finish Regular Quiz Games',
      stat: { event: 'quiz-default', selector: 'finished', min_value: 0, max_value: 1000000000 },
      path: false
    },
    {
      name: 'Finish Exam Quiz Games',
      stat: { event: 'quiz-exam', selector: 'finished', min_value: 0, max_value: 1000000000 },
      path: false
    },
    {
      name: 'Finish Review Quiz Games',
      stat: { event: 'quiz-review', selector: 'finished', min_value: 0, max_value: 1000000000 },
      path: false
    },
    {
      name: 'Finish Case Study Quiz Games',
      stat: { event: 'quiz-casestudy', selector: 'finished', min_value: 0, max_value: 1000000000 },
      path: false
    },
    {
      name: 'Finish Multiplayer Quiz Games',
      stat: { event: 'quiz-multiplayer', selector: 'finished', min_value: 0, max_value: 1000000000 },
      path: false
    },
    {
      name: 'Win Multiplayer Quiz Games',
      stat: { event: 'quiz-multiplayer', selector: 'won', min_value: 0, max_value: 1000000000 },
      path: false
    },
    {
      name: 'Lose Multiplayer Quiz Games',
      stat: { event: 'quiz-multiplayer', selector: 'lost', min_value: 0, max_value: 1000000000 },
      path: false
    },
    {
      name: 'View Flashcards',
      stat: { event: 'flashcard-open', selector: '', min_value: 0, max_value: 1000000000 },
      path: false
    },
    {
      name: 'Flip Flashcards',
      stat: { event: 'flashcard-flip', selector: '', min_value: 0, max_value: 1000000000 },
      path: false
    },
    {
      name: 'Proceed Flashcards',
      stat: { event: 'flashcard-next', selector: '', min_value: 0, max_value: 1000000000 },
      path: false
    }
  ];

  public tree: Tree;
  public achievement: Achievement = null;
  public path: NestedPathElement;
  public loading = false;

  public event: boolean = false;
  public eventStartDate: NgbDateStruct;
  public eventStartTime: NgbTimeStruct;
  public eventEndDate: NgbDateStruct;
  public eventEndTime: NgbTimeStruct;
  public notificationEnabled: boolean = false;
  public notificationTitle: string;
  public notificationMessage: string;
  public scoreEnabled: boolean = false;
  public scoreValue: number = 0;
  public statUnlockValue: number;
  public stat: AchievementStat;
  private _statOptionIndex: number = 0;

  public get statOptionIndex(): number {
    return this._statOptionIndex;
  }

  public set statOptionIndex(index: number) {
    this._statOptionIndex = index;
    this.stat = { ...this.statOptions[index].stat };
  }

  public get statOption(): StatOption {
    return this.statOptions[this._statOptionIndex];
  }

  public get title(): string {
    return this.getLocalized(this.achievement.i18n_achieved, 'title');
  }

  public set title(val: string) {
    this.achievement.i18n_achieved = this.applyLocalized(this.achievement.i18n_achieved, 'title', val);
    this.achievement.i18n_locked = this.applyLocalized(this.achievement.i18n_locked, 'title', val);
  }

  public get descriptionLocked(): string {
    return this.getLocalized(this.achievement.i18n_locked, 'description');
  }

  public set descriptionLocked(val: string) {
    this.achievement.i18n_locked = this.applyLocalized(this.achievement.i18n_locked, 'description', val);
  }

  public get descriptionAchieved(): string {
    return this.getLocalized(this.achievement.i18n_achieved, 'description');
  }

  public set descriptionAchieved(val: string) {
    this.achievement.i18n_achieved = this.applyLocalized(this.achievement.i18n_achieved, 'description', val);
  }

  public get languageHint(): string {
    return environment.languages.length > 1 ? ` (${this.cms.language})` : '';
  }

  public get maxDepth(): number {
    return this.cms.config.maxDepth();
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private cms: CmsService,
    private server: CmsServerService,
    private notification: NotificationsService
  ) {}

  public ngOnInit() {
    this.route.paramMap.subscribe(map => void this.init(map.get('uuid')));
    this.cms.tree.asObservable().subscribe(tree => {
      this.tree = tree;
      this.path = { node: tree.root, children: [] };
    });
  }

  private async init(uuid: string) {
    this.loading = true;
    const all = await this.server.achievementList();
    if (uuid === 'new') {
      this.achievement = {
        name: 'New_Achievement',
        type: 'achievement',
        effects: [],
        display: AchievementDisplay.Show,
        repeatable: false,
        unlock_value: 1
      };
      this.statOptionIndex = 0; // default to total online time
      this.loading = false;
    } else {
      this.achievement = all.find(j => j.uuid === uuid);
      if (this.achievement == null) {
        return this.cancel();
      }

      this.stat = this.achievement.stat;
      this._statOptionIndex =
        this.statOptions.findIndex(opt => opt.stat.event == this.stat.event && (!!opt.path || opt.stat.selector == this.stat.selector)) ??
        0;
      if (this.statOptions[this._statOptionIndex].path) {
        this.path.node = this.tree.getByUUID(this.stat.selector) ?? this.tree.root;
      }

      const scoreEffect = this.achievement.effects.find(e => e.type == 'score');
      if (!!scoreEffect?.value) {
        this.scoreEnabled = true;
        this.scoreValue = scoreEffect.value ?? 0;
      } else {
        this.scoreEnabled = false;
        this.scoreValue = 0;
      }

      const notificationEffect = this.achievement.effects.find(e => e.type == 'notification');
      if (!!notificationEffect?.value) {
        this.notificationEnabled = true;
        this.notificationTitle = this.getLocalized(notificationEffect.value, 'title');
        this.notificationMessage = this.getLocalized(notificationEffect.value, 'message');
      } else {
        this.notificationEnabled = false;
        this.notificationTitle = null;
        this.notificationMessage = null;
      }

      this.eventStartDate = this.toDate(this.achievement.event_start);
      this.eventStartTime = this.toTime(this.achievement.event_start);
      this.eventEndDate = this.toDate(this.achievement.event_end);
      this.eventEndTime = this.toTime(this.achievement.event_end);
      this.event = !!this.eventStartDate || !!this.eventEndDate;

      this.loading = false;
    }
  }

  public async submit() {
    this.loading = true;
    const create = !this.achievement.uuid;
    // ensure a title is set
    if (create && !this.title) {
      this.title = this.achievement.name;
    }
    // patch stat
    this.achievement.stat = this.getPatchedStat();
    // setup notification
    if (this.notificationEnabled && this.notificationTitle && this.notificationMessage) {
      var effect = this.achievement.effects.find(e => e.type === 'notification');
      if (!effect) {
        effect = { type: 'notification', value: {} };
        this.achievement.effects.push(effect);
      }
      this.applyLocalized(effect.value, 'title', this.notificationTitle);
      this.applyLocalized(effect.value, 'message', this.notificationMessage);
    } else {
      this.achievement.effects = this.achievement.effects.filter(e => e.type !== 'notification');
    }
    // setup score
    if (this.scoreEnabled && this.scoreValue > 0) {
      var effect = this.achievement.effects.find(e => e.type === 'score');
      if (!effect) {
        effect = { type: 'score', value: {} };
        this.achievement.effects.push(effect);
      }
      effect.value = this.scoreValue;
    } else {
      this.achievement.effects = this.achievement.effects.filter(e => e.type !== 'score');
    }
    // setup event
    if (this.event) {
      this.achievement.event_start = this.fromDateTime(this.eventStartDate, this.eventStartTime);
      this.achievement.event_end = this.fromDateTime(this.eventEndDate, this.eventEndTime);
    } else {
      this.achievement.event_start = null;
      this.achievement.event_end = null;
    }
    try {
      await this.server.achievementUpsert([this.achievement]);
      await this.notification.notify(
        'success',
        create ? 'Created' : 'Updated',
        `The Achievement was successfully ${create ? 'created' : 'updated'}!`
      );
      await this.router.navigate(['/achievement']);
    } finally {
      this.loading = false;
    }
  }

  private getPatchedStat(): AchievementStat {
    const ach = this.achievement.stat;
    if (
      this.stat.event === ach?.event &&
      this.stat.selector !== ach.selector &&
      this.event === (!!this.eventStartDate || !!this.eventEndDate)
    ) {
      // The event has not been changed
      return ach;
    }
    return {
      ...this.stat,
      uuid: null,
      context: this.event ? this.achievement.name : ''
    };
  }

  public cancel() {
    return this.router.navigate(['/achievement']);
  }

  private getLocalized(container: any, key: string): string {
    const lang = this.cms.language;
    return container && container[lang] ? container[lang][key] : null;
  }

  private applyLocalized(container: any, key: string, value: string): any {
    const lang = this.cms.language;
    if (!value || value === '') {
      // delete key
      if (container && container[lang]) {
        delete container[lang][key];
        if (Object.keys(container[lang]).length === 0) delete container[lang];
        return container;
      }
    } else {
      // put key
      if (!container) container = {};
      if (!container[lang]) container[lang] = {};
      container[lang][key] = value;
    }
    return container;
  }

  private toDate(i: Instant): NgbDateStruct {
    if (!i) return null;
    const d = new Date(i);
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  }

  private toTime(i: Instant): NgbTimeStruct {
    if (!i) return null;
    const d = new Date(i);
    return { hour: d.getUTCHours(), minute: d.getUTCMinutes(), second: d.getUTCSeconds() };
  }

  private fromDateTime(d: NgbDateStruct, t: NgbTimeStruct): Instant {
    return d ? new Date(Date.UTC(d.year, d.month - 1, d.day, t?.hour ?? 0, t?.minute ?? 0)).toJSON() : null;
  }
}
